import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Plane } from "lucide-react";

const parse = (s) => { const [a, b] = String(s || "").split(",").map(Number); return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null; };
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

export const distanceKm = ([la1, lo1], [la2, lo2]) => {
  const h = Math.sin(rad(la2 - la1) / 2) ** 2 + Math.cos(rad(la1)) * Math.cos(rad(la2)) * Math.sin(rad(lo2 - lo1) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

const arc = ([la1, lo1], [la2, lo2], steps = 64) => {
  const [p1, l1, p2, l2] = [rad(la1), rad(lo1), rad(la2), rad(lo2)];
  const d = 2 * Math.asin(Math.sqrt(Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2));
  if (!d) return [[la1, lo1], [la2, lo2]];
  const pts = [];
  let prev = null;
  for (let i = 0; i <= steps; i++) {
    const f = i / steps, A = Math.sin((1 - f) * d) / Math.sin(d), B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(p1) * Math.cos(l1) + B * Math.cos(p2) * Math.cos(l2);
    const y = A * Math.cos(p1) * Math.sin(l1) + B * Math.cos(p2) * Math.sin(l2);
    const z = A * Math.sin(p1) + B * Math.sin(p2);
    let lon = deg(Math.atan2(y, x));
    if (prev !== null) while (lon - prev > 180) lon -= 360; while (prev !== null && prev - lon > 180) lon += 360;
    prev = lon;
    pts.push([deg(Math.atan2(z, Math.sqrt(x * x + y * y))), lon]);
  }
  return pts;
};

export const flightTime = (km) => { const mins = Math.round((km / 820) * 60 + 25); return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`; };

const short = (label) => String(label || "").replace(/\s*\([A-Z]{3}\)\s*$/, "") || "?";
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");

export const RouteMap = ({ points = [], testId = "route-map" }) => {
  const el = useRef(null);
  const pts = points.map((p) => ({ ...p, c: parse(p.ll) })).filter((p) => p.c);
  const key = pts.map((p) => `${p.ll}|${p.label}`).join(";");
  useEffect(() => {
    if (pts.length < 2 || !el.current) return undefined;
    const map = L.map(el.current, { zoomControl: false, attributionControl: true, dragging: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, touchZoom: false });
    L.tileLayer("https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", { attribution: "Tiles © Esri", maxZoom: 10 }).addTo(map);
    const dot = { radius: 6, color: "#0b0f0e", weight: 2, fillColor: "#3ee6b8", fillOpacity: 1 };
    const un = [];
    pts.forEach((p, i) => {
      let lon = p.c[1];
      if (i) { while (lon - un[i - 1][1] > 180) lon -= 360; while (un[i - 1][1] - lon > 180) lon += 360; }
      un.push([p.c[0], lon]);
    });
    const all = [];
    un.slice(1).forEach((c, i) => {
      const raw = arc(pts[i].c, pts[i + 1].c);
      const off = un[i][1] - raw[0][1];
      const seg = raw.map(([la, lo]) => [la, lo + off]);
      all.push(...seg);
      L.polyline(seg, { color: "#3ee6b8", weight: 2.5, dashArray: "6 6" }).addTo(map);
    });
    un.forEach((c, i) => L.circleMarker(c, dot).addTo(map).bindTooltip(short(pts[i].label), { permanent: true, direction: i % 2 ? "bottom" : "top", className: "route-tip" }));
    map.fitBounds(L.latLngBounds(all), { padding: [36, 36], maxZoom: 8 });
    return () => map.remove();
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (pts.length < 2) return null;
  const legs = pts.slice(1).map((p, i) => ({ from: pts[i], to: p, km: distanceKm(pts[i].c, p.c) }));
  const total = legs.reduce((t, l) => t + l.km, 0);
  const mins = legs.reduce((t, l) => t + Math.round((l.km / 820) * 60 + 25), 0);
  return (
    <div className="overflow-hidden rounded-lg border border-border" data-testid={testId}>
      <div ref={el} className="h-[220px] w-full bg-[#0b0f0e]" role="img" aria-label={`Flight route ${pts.map((p) => short(p.label)).join(" to ")}`} />
      {legs.length > 1 && (
        <ol className="divide-y divide-border border-t border-border text-sm" data-testid={`${testId}-legs`}>
          {legs.map((l, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2" data-testid={`${testId}-leg-${i}`}>
              <span className="w-12 text-xs uppercase tracking-wider text-muted-foreground">Leg {i + 1}</span>
              <span className="font-semibold">{short(l.from.label)} → {short(l.to.label)}</span>
              {l.from.date && <span className="text-[hsl(var(--gold))]" data-testid={`${testId}-leg-${i}-date`}>{fmtDate(l.from.date)}</span>}
              <span className="ml-auto text-muted-foreground">{Math.round(l.km).toLocaleString("en-GB")} km · ≈ {flightTime(l.km)}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-2.5 text-sm">
        <Plane className="h-4 w-4 text-primary" />
        {legs.length > 1 && <span className="text-xs uppercase tracking-wider text-muted-foreground">Total</span>}
        <span data-testid={`${testId}-distance`}>{Math.round(total).toLocaleString("en-GB")} km · {Math.round(total * 0.54).toLocaleString("en-GB")} nm</span>
        <span className="text-muted-foreground" data-testid={`${testId}-time`}>≈ {Math.floor(mins / 60)}h {String(mins % 60).padStart(2, "0")}m flying</span>
        {legs.length === 1 && legs[0].from.date && <span className="text-[hsl(var(--gold))]" data-testid={`${testId}-leg-0-date`}>{fmtDate(legs[0].from.date)}</span>}
      </div>
    </div>
  );
};
